import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { WAITING_TIME } from 'coreOld/constants';
import { ApiStatuses, NotificationPosition } from 'coreOld/enums';
import { useNotification } from 'coreOld/hooks/useNotification';
import { useCompanyPartsActions } from 'partsOld/hooks';
import { mapCompanyPartToPartForm, mapCompanyPartToServer } from 'partsOld/mappers';
import { ICompanyPart, ICompanyPartForm, IPart } from 'partsOld/types';

import { companyPartDetailsFormSchema } from './companyPartDetailsFormSchema';
import CompanyPartDetailsFormView from './CompanyPartDetailsFormView';

interface IProps {
  onClosePanel: () => void;
  part?: Partial<ICompanyPart>;
  searchedPart?: IPart;
  isLoading?: boolean;
  isError?: boolean;
  isAddMode?: boolean;
}

const CompanyPartDetailsForm = ({
  onClosePanel,
  part,
  isLoading,
  isError,
  isAddMode,
  searchedPart,
}: IProps) => {
  const { upsertCompanyPart, upsertIsLoading } = useCompanyPartsActions();
  const { showNotification } = useNotification();

  const [isEditable, setIsEditable] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<ICompanyPartForm>({
    resolver: yupResolver(companyPartDetailsFormSchema),
    defaultValues: mapCompanyPartToPartForm(part),
  });

  const onSubmit = async (data: ICompanyPartForm) => {
    let dataToSend;

    if (data?.id) {
      dataToSend = data;
    } else {
      dataToSend = {
        ...data,
        part: searchedPart.id,
        manufacturerId: searchedPart.manufacturerId,
      };
    }

    await upsertCompanyPart(mapCompanyPartToServer(dataToSend));

    if (isAddMode) {
      setTimeout(() => {
        onClosePanel();
      }, WAITING_TIME);
    }

    setIsEditable(false);
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorsMessage = errors && Object.values(errors);

      showNotification({
        message: errorsMessage?.[0]?.message,
        type: ApiStatuses.ERROR,
        position: NotificationPosition.TAB,
      });

      return;
    }

    showNotification(null);
  }, [errors]);

  return (
    <CompanyPartDetailsFormView
      onSubmit={onSubmit}
      control={control}
      handleSubmit={handleSubmit}
      isEditable={isEditable}
      setIsEditable={setIsEditable}
      part={part}
      getValues={getValues}
      isLoading={isLoading}
      isError={isError}
      isSubmitting={isSubmitting && upsertIsLoading}
      isAddMode={isAddMode}
      isSaveEnabled={Boolean(part || searchedPart)}
    />
  );
};

export default CompanyPartDetailsForm;
